import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  getAllRestaurantsFinance,
  getRestaurantFinanceDetails,
  getAllPayoutHistory,
  getPayoutReceipt,
  RestaurantFinanceItem,
  PayoutHistoryItem,
} from "../../../redux/restaurantFinance/restaurantFinanceSlice";
import {
  formatCurrency,
  formatNumber,
  formatFinanceDataForView,
  formatPayoutReceiptForModal,
  EarningsView,
} from "./data";
import LoadingSpinner from "../../../components/elements/LoadingSpinner";
import FinancialDetails from "../../../components/modals/Restaurant/FinancialDetails";
import Receipt from "../../../components/modals/Restaurant/Receipt";
import { BsEye } from "react-icons/bs";

type TableType = "earnings" | "payouts";
type RowData = RestaurantFinanceItem | PayoutHistoryItem;

const isEarnings = (data: RowData): data is RestaurantFinanceItem =>
  (data as RestaurantFinanceItem).restaurantGeneratedID !== undefined;

const Table = ({ table }: { table: TableType }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { 
    restaurants, 
    payoutHistory, 
    status, 
    payoutHistoryStatus, 
    currentRestaurantDetails 
  } = useSelector((state: RootState) => state.restaurantFinance);

  // Debug logging
  console.log('🔍 Table Component State:', {
    table,
    payoutHistoryLength: payoutHistory.length,
    payoutHistoryStatus,
    restaurantsLength: restaurants.length,
    status
  });

  const [selectedEarnings, setSelectedEarnings] = useState<EarningsView | null>(
    null
  );
  const [selectedPayout, setSelectedPayout] = useState<any>(null);

  useEffect(() => {
    if (table === "earnings") {
      dispatch(getAllRestaurantsFinance({ page: 1, limit: 50 }));
    } else if (table === "payouts") {
      dispatch(getAllPayoutHistory({ page: 1, limit: 50 }));
    }
  }, [table, dispatch]);

  const data = table === "earnings" ? restaurants : payoutHistory;
  const isLoading = table === "earnings" ? status === "loading" : payoutHistoryStatus === "loading";

  const handleViewEarnings = async (restaurant: RestaurantFinanceItem) => {
    // Fetch detailed data for the restaurant
    const result = await dispatch(
      getRestaurantFinanceDetails(restaurant.restaurantGeneratedID)
    );
    if (getRestaurantFinanceDetails.fulfilled.match(result)) {
      const earningsView = formatFinanceDataForView(
        restaurant,
        result.payload.data
      );
      setSelectedEarnings(earningsView);
    }
  };

  const handleViewPayout = async (payout: PayoutHistoryItem) => {
    // Create a proper receipt object from payout data
    const payoutReceipt = {
      vendorDetails: {
        name: payout.restaurant || "Unknown Restaurant",
        id: payout.restaurantId || "N/A",
        phone: "",
        email: "",
      },
      payoutInfo: {
        amount: (payout.amount || 0).toString(),
        date: payout.date || new Date().toISOString(),
        bankName: payout.bankDetails?.bankName || "N/A",
        accountNumber: payout.bankDetails?.accountNumber?.toString() || "N/A",
        reference: payout.reference || "N/A",
        status: payout.status || "N/A",
      },
      totalEarnings: (payout.grossAmount || 0).toString(),
      deduction: (payout.commission || 0).toString(),
      netPayout: (payout.amount || 0).toString(),
      description: payout.description || "Restaurant payout",
    };
    setSelectedPayout(payoutReceipt);
  };

  const renderTableHeader = () =>
    table === "earnings" ? (
      <>
        <th className="p-3 font-medium">Restaurant</th>
        <th className="p-3 font-medium">Total Earnings</th>
        <th className="p-3 font-medium">Pending Payout</th>
        <th className="p-3 font-medium">Growth</th>
        <th className="p-3 font-medium">Commission</th>
        <th className="p-3 font-medium">Status</th>
        <th className="p-3 font-medium">Action</th>
      </>
    ) : (
      <>
        <th className="p-3 font-medium">Reference</th>
        <th className="p-3 font-medium">Restaurant</th>
        <th className="p-3 font-medium">Amount</th>
        <th className="p-3 font-medium">Gross Amount</th>
        <th className="p-3 font-medium">Commission</th>
        <th className="p-3 font-medium">Date</th>
        <th className="p-3 font-medium">Status</th>
        <th className="p-3 font-medium">Account</th>
        <th className="p-3 font-medium">Action</th>
      </>
    );

  const renderRow = (row: RowData) => {
    console.log('🔍 Rendering Row:', { 
      rowData: row, 
      isEarnings: isEarnings(row),
      table,
      hasRestaurantGeneratedID: !!(row as RestaurantFinanceItem).restaurantGeneratedID,
      hasReference: !!(row as PayoutHistoryItem).reference
    });

    // Debug payout data fields
    if (!isEarnings(row)) {
      const payoutRow = row as PayoutHistoryItem;
      console.log('🔶 Reference:', payoutRow.reference, typeof payoutRow.reference);
      console.log('🏢 Restaurant:', payoutRow.restaurant, typeof payoutRow.restaurant);
      console.log('💰 Amount:', payoutRow.amount, typeof payoutRow.amount);
      console.log('💵 Gross Amount:', payoutRow.grossAmount, typeof payoutRow.grossAmount);
      console.log('💸 Commission:', payoutRow.commission, typeof payoutRow.commission);
      console.log('📅 Date:', payoutRow.date, typeof payoutRow.date);
      console.log('✅ Status:', payoutRow.status, typeof payoutRow.status);
      console.log('🏦 Bank Details:', payoutRow.bankDetails);
    }
    
    return isEarnings(row) ? (
      <tr key={row.id} className="border text-sm">
        <td className="p-5">{row.name || "N/A"}</td>
        <td className="p-5">{formatCurrency(row.totalEarnings || 0)}</td>
        <td className="p-5">{formatCurrency(row.pendingPayouts || 0)}</td>
        <td className="p-5">{row.growth || "0%"}</td>
        <td className="p-5">{formatCurrency(row.commission || 0)}</td>
        <td className="p-5">
          <p
            className={`${
              row.status === "active"
                ? "text-secondary"
                : row.status === "inactive"
                ? "text-red-500"
                : ""
            } text-center font-semibold rounded-md p-1 text-[12px] capitalize`}
          >
            {row.status}
          </p>
        </td>
        <td className="p-5">
          <button
            className="text-secondary border-secondary border flex items-center gap-2 text-sm py-1 px-5 rounded-lg"
            onClick={() => handleViewEarnings(row)}
          >
            <BsEye /> View
          </button>
        </td>
      </tr>
    ) : (
      <tr key={row.id} className="border text-sm">
        <td className="p-5">
          <span className="font-medium text-blue-600">{row.reference || "N/A"}</span>
        </td>
        <td className="p-5">{row.restaurant || "N/A"}</td>
        <td className="p-5">
          <span className="font-semibold text-green-600">
            {formatCurrency(row.amount || 0)}
          </span>
        </td>
        <td className="p-5">
          <span className="text-gray-600">
            {formatCurrency(row.grossAmount || 0)}
          </span>
        </td>
        <td className="p-5">
          <span className="text-orange-600">
            {formatCurrency(row.commission || 0)}
          </span>
        </td>
        <td className="p-5">
          <span className="text-xs text-gray-500">
            {row.date ? new Date(row.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            }) : "N/A"}
          </span>
        </td>
        <td className="p-5">
          <span
            className={`${
              row.status === "completed"
                ? "bg-green-100 text-green-600"
                : row.status === "pending"
                ? "bg-yellow-100 text-yellow-600"
                : row.status === "failed"
                ? "bg-red-100 text-red-600"
                : "bg-gray-100 text-gray-600"
            } text-center rounded-md px-2 py-1 text-xs font-medium capitalize`}
          >
            {row.status}
          </span>
        </td>
        <td className="p-5">
          <div className="text-xs">
            <div className="font-medium">{row.bankDetails?.bankName || "N/A"}</div>
            <div className="text-gray-500">{row.bankDetails?.accountNumber || "N/A"}</div>
          </div>
        </td>
        <td className="p-5">
          <button
            className="text-secondary border border-secondary rounded-md px-3 py-1 flex items-center gap-1 text-xs hover:bg-secondary hover:text-white transition-colors"
            onClick={() => handleViewPayout(row)}
          >
            <BsEye />
            View
          </button>
        </td>
      </tr>
    );
  };

  return (
    <section>
      <table className="w-full">
        <thead className="font-normal">
          <tr className="bg-white">{renderTableHeader()}</tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={table === "earnings" ? 7 : 9} className="text-center py-4">
                <LoadingSpinner />
              </td>
            </tr>
          ) : data.length > 0 ? (
            data.map(renderRow)
          ) : (
            <tr>
              <td colSpan={table === "earnings" ? 7 : 9} className="text-center py-4">
                No {table === "earnings" ? "earnings" : "payouts"} found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {selectedEarnings && (
        <FinancialDetails
          earningDetails={selectedEarnings}
          toggleModal={() => setSelectedEarnings(null)}
        />
      )}
      {selectedPayout && (
        <Receipt
          payout={selectedPayout}
          toggleModal={() => setSelectedPayout(null)}
        />
      )}
    </section>
  );
};

export default Table;
